import { Fab, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { MouseEvent, useMemo, useState } from 'react';
import { SECURITY_ACTIONS, useEntitySecurityEvaluator, type SecurityTarget } from '../auth/Security';
import { Icons } from '../icons/Icons';
import type { SecuredActionType } from './ActionType';

type AddActionsFabProps = {
    /**
     * Menu / single-click add actions. Filtered by `securityAction` (defaults to EDIT) via the
     * centralized evaluator. Hidden actions are omitted.
     */
    securedActions?: SecuredActionType[];

    /**
     * Fallback when there are no resolved `securedActions`. Used for the FAB click path only.
     * Visibility and invocation are gated by EDIT against `defaultAddSecurityTarget` (or ambient
     * feature context when the target is omitted).
     */
    defaultAddAction?: () => void;

    /**
     * Security target for evaluating EDIT on `defaultAddAction`. When omitted, feature permissions
     * are resolved from ambient `FeatureSecurityContext`; entity permissions are evaluated only
     * when `entityName` is provided, using explicit `entitySecurity` or ambient `EntitySecurityContext`.
     */
    defaultAddSecurityTarget?: SecurityTarget;
};

export default function AddActionsFab({
    securedActions = [],
    defaultAddAction,
    defaultAddSecurityTarget
}: AddActionsFabProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);
    const evaluateEntitySecurity = useEntitySecurityEvaluator();

    const resolvedActions = useMemo(() => {
        const actions = securedActions.filter((action) => {
            if (action.hidden) {
                return false;
            }

            const decision = evaluateEntitySecurity(action.securityAction ?? SECURITY_ACTIONS.EDIT, {
                entityName: action.entityName,
                entitySecurity: action.entitySecurity,
                featureName: action.featureName
            });

            return Boolean(decision.allowed && !decision?.pending);
        });

        return actions;
    }, [evaluateEntitySecurity, securedActions]);

    // defaultAddAction is not in securedActions, so evaluate EDIT separately for FAB visibility and the fallback click.
    const canDefaultAdd = useMemo(() => {
        const decision = evaluateEntitySecurity(SECURITY_ACTIONS.EDIT, defaultAddSecurityTarget ?? {});
        const isAllowed = Boolean(decision.allowed && !decision?.pending);

        return isAllowed;
    }, [defaultAddSecurityTarget, evaluateEntitySecurity]);

    const isDisabled = !!resolvedActions?.length && resolvedActions.every((action) => action.disabled);

    if (resolvedActions.length === 0 && !(defaultAddAction && canDefaultAdd)) {
        return null;
    }

    return (
        <>
            <Fab
                sx={{ width: '32px', height: '32px', minWidth: '32px', minHeight: '32px', zIndex: '1' }}
                color="primary"
                aria-label="add"
                variant={'circular'}
                disabled={isDisabled}
                onClick={(event: MouseEvent<HTMLElement>) => {
                    if (isDisabled) return;
                    if (resolvedActions?.length === 1 && typeof resolvedActions[0].onClick === 'function') {
                        // 1 action with onClick handler.
                        resolvedActions?.[0].onClick();
                    } else if (resolvedActions?.length > 1) {
                        // Multiple actions, show menu.
                        setAnchorEl(event.currentTarget);
                    } else if (typeof defaultAddAction === 'function' && canDefaultAdd) {
                        // No actions but there is default function.
                        defaultAddAction();
                    } else {
                        console.warn('Failed to handle actions:', resolvedActions);
                    }
                }}
            >
                <Icons.AddOutlined />
            </Fab>

            {isMenuOpen && (
                <Menu
                    sx={{ mt: 0.5 }}
                    anchorEl={anchorEl}
                    open
                    onClose={() => {
                        setAnchorEl(null);
                    }}
                >
                    {resolvedActions.map((action, index) => (
                        <MenuItem
                            key={index}
                            disabled={action.disabled}
                            onClick={() => {
                                if (action.disabled) return;
                                action.onClick?.();
                                setAnchorEl(null);
                            }}
                        >
                            {action.icon && (
                                <ListItemIcon>
                                    <action.icon />
                                </ListItemIcon>
                            )}
                            <ListItemText>{action.label}</ListItemText>
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    );
}
